<template>
  <div class="dropdown-container">
    <UiLabel
      v-if="label.position === LabelPosition.LEFT"
      :text="label.text"
      class="label"
    />
    <div class="select-container">
      <div class="tag-container">
        <select
          v-model="computedValue"
          class="select"
          :aria-label="label.text"
          :class="label.text === computedValue ? 'headline-m' : 'body-m'"
        >
          <option v-if="isInnerLabel" disabled selected hidden>
            {{ label.text }}
          </option>
          <option
            v-for="option in dropdownList"
            :key="option.value"
            :value="option.value"
            :selected="option.isSelected"
          >
            {{ option.label }}
          </option>
        </select>
      </div>
      <UiIcon name="caret" size="12px" class="dropdown-icon" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'
import UiLabel from '@sephora-asia/ui-label/UiLabel.vue'
import { LabelPosition } from './enums'
import type { DropdownOption, Label } from './types'
import type { WritableComputedRef } from 'vue'

interface Props {
  label: Label
  dropdownList: DropdownOption[]
}

const emit = defineEmits(['update:selectedOption'])

const props = defineProps<Props>()

const isInnerLabel = computed<boolean>(() => {
  return props.label.position === LabelPosition.IN
})

const computedValue: WritableComputedRef<string | number> = computed({
  get(): string | number {
    return (
      props.dropdownList.find(option => option.isSelected)?.value ||
      (isInnerLabel.value ? props.label.text : props.dropdownList[0].value)
    )
  },
  set(value: string | number): void {
    emit('update:selectedOption', value)
  },
})
</script>
<style lang="scss" scoped>
.dropdown-container {
  display: flex;
  align-items: center;

  .label {
    font-weight: var(--size-typography-weight-bold);
    margin-right: 8px;
    cursor: default;
  }

  .select-container {
    height: 40px;
    outline: 0;
    border-radius: var(--size-radius-medium);
    position: relative;
    display: flex;

    .tag-container {
      display: flex;
      align-items: center;

      .select {
        display: inline-block;
        height: 40px;
        appearance: none;
        text-overflow: ellipsis;
        outline: 0;
        border-radius: var(--size-radius-medium);
        background: transparent;
        padding: 9.5px 35px 9.5px 12px;
        text-transform: none;
      }
    }

    .dropdown-icon {
      position: absolute;
      top: 14px;
      right: 14px;
    }
  }
}
</style>
